import React, { useState } from "react";
import actsData from "./actsData.json";
import PageTitle from "../../../components/pagetitle/PageTitle";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import DescriptionIcon from "@mui/icons-material/Description";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const Acts = () => {
  const location = useLocation();

  // Restore previous tab & expandedAct if coming back from ActInfoPage
  const restoredTabId = location.state?.activeTab || actsData[0].id;
  const initialTabIndex = actsData.findIndex(
    (item) => item.id === restoredTabId
  );
  const restoredExpandedAct = location.state?.expandedAct || null;

  const [activeTab, setActiveTab] = useState(
    initialTabIndex !== -1 ? initialTabIndex : 0
  );
  const [expandedAct, setExpandedAct] = useState(restoredExpandedAct);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setExpandedAct(null);
  };

  const currentCategory = actsData[activeTab];

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh", pb: 10 }}>
      <PageTitle pageTitle="Legal Acts & Statutes" pagesub="Knowledge Bank" />

      <Container
        maxWidth="lg"
        sx={{ mt: { xs: -4, md: -6 }, position: "relative", zIndex: 10 }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 10px 40px rgba(0,0,0,0.05)",
            border: "1px solid #eee",
            bgcolor: "#fff",
          }}
        >
          {/* Top Tabs */}
          <Box
            sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#fff" }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-indicator": {
                  height: 4,
                  borderRadius: "4px 4px 0 0",
                  backgroundColor: "var(--primary-color)",
                },
                "& .MuiTab-root": {
                  py: 3,
                  fontSize: "1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#666",
                  "&.Mui-selected": {
                    color: "var(--primary-color)",
                  },
                },
              }}
            >
              {actsData.map((item) => (
                <Tab key={item.id} label={item.title} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                fontWeight: 800,
                color: "#333",
                fontFamily: "'Roboto Slab', serif",
              }}
            >
              Explore {currentCategory.title}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {currentCategory.categories.map((act) => (
                <Accordion
                  key={act.id}
                  expanded={expandedAct === act.id}
                  onChange={() =>
                    setExpandedAct(expandedAct === act.id ? null : act.id)
                  }
                  elevation={0}
                  sx={{
                    border: "1px solid #eff1f5",
                    borderRadius: "16px !important",
                    overflow: "hidden",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": {
                      boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
                      borderColor: "var(--primary-color)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon sx={{ color: "var(--primary-color)" }} />
                    }
                    sx={{
                      px: 3,
                      py: 1,
                      "&.Mui-expanded": { bgcolor: "#fcfdff" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <MenuBookIcon
                        sx={{
                          color:
                            expandedAct === act.id
                              ? "var(--primary-color)"
                              : "#999",
                        }}
                      />
                      <Typography sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
                        {act.title}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, py: 0 }}>
                    <Divider />
                    <List disablePadding>
                      {act.chapters.map((chap, chIndex) => (
                        <ListItem key={chIndex} disablePadding>
                          <ListItemButton
                            component={Link}
                            to={`/acts/${currentCategory.id}/${act.id}/${chIndex}`}
                            state={{
                              chapter: chap,
                              activeTab: currentCategory.id,
                              expandedAct: act.id,
                            }}
                            sx={{
                              px: 4,
                              py: 2,
                              "&:hover": { bgcolor: "#f8faff" },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <DescriptionIcon
                                sx={{ fontSize: "1.2rem", color: "#666" }}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={chap.title}
                              primaryTypographyProps={{
                                fontWeight: 500,
                                color: "#444",
                              }}
                              secondary={chap.info}
                              secondaryTypographyProps={{
                                sx: {
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  mt: 0.5,
                                  fontSize: "0.85rem",
                                  opacity: 0.8,
                                },
                              }}
                            />
                            <ChevronRightIcon sx={{ color: "#ccc" }} />
                          </ListItemButton>
                          {chIndex < act.chapters.length - 1 && (
                            <Divider
                              component="li"
                              inset="left"
                              sx={{ ml: 9 }}
                            />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        </Paper>

        <Box sx={{ mt: 6, textAlign: "center" }}>
          <Typography
            variant="body2"
            sx={{
              color: "#777",
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            Disclaimer: The summaries provided herein reflect statutory language
            and are intended for general guidance only. For specific legal
            interpretations, please refer to the latest official gazette
            notifications.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Acts;
